/**
 * Client-side helpers for orders UI demo rows (until APIs wire filters).
 */

/** YYYY-MM-DD in local TZ — matches table times built from local date parts and native `<input type="date">`. */
export function localCalendarDateYYYYMMDD(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Last N calendar days through today (local). Avoids `toISOString().slice(0,10)` UTC drift vs displayed row times. */
export function spotActivityDefaultLocalRange(daysBack = 30) {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - daysBack);
  return { from: localCalendarDateYYYYMMDD(from), to: localCalendarDateYYYYMMDD(to) };
}

export function parseRowDate(input) {
  if (!input) return null;
  const s = String(input).trim();
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const iso = s.includes(" ") ? s.replace(" ", "T") : `${s}T12:00:00`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Time dropdown keys: "7d" | "30d" | "90d" or labels like "Past 30 days" */
export function rowWithinPastDays(rowTimeStr, rangeKey) {
  const d = parseRowDate(rowTimeStr);
  if (!d) return true;
  const map = {
    "7d": 7,
    "30d": 30,
    "90d": 90,
    "Past 7 days": 7,
    "Past 30 days": 30,
    "Past 90 days": 90,
  };
  const days = map[rangeKey] ?? 30;
  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() - days);
  return d >= cutoff;
}

/** Date inputs YYYY-MM-DD */
export function rowBetweenDateInputs(rowTimeStr, dateFrom, dateTo) {
  const d = parseRowDate(rowTimeStr);
  if (!d) return true;
  if (dateFrom) {
    const from = new Date(`${dateFrom}T00:00:00`);
    if (d < from) return false;
  }
  if (dateTo) {
    const to = new Date(`${dateTo}T23:59:59`);
    if (d > to) return false;
  }
  return true;
}

export function matchesDepositCoin(rowCoinSub, rowCoinName, filterCoin) {
  if (!filterCoin || filterCoin === "all") return true;
  const sub = (rowCoinSub || "").toLowerCase();
  const nm = (rowCoinName || "").toLowerCase();
  const aliases = {
    btc: ["btc", "bitcoin"],
    eth: ["eth", "ethereum"],
    matic: ["matic", "polygon"],
    ltc: ["ltc", "litecoin"],
  };
  const keys = aliases[filterCoin] || [filterCoin.toLowerCase()];
  return keys.some((k) => sub === k || nm.includes(k));
}

export function matchesStatusNorm(rowStatus, filterStatus) {
  if (!filterStatus || filterStatus === "all") return true;
  return String(rowStatus || "")
    .toLowerCase()
    .includes(String(filterStatus).toLowerCase());
}

export function matchesTypeNorm(rowType, filterType) {
  if (!filterType) return true;
  return String(rowType || "").toLowerCase() === String(filterType).toLowerCase();
}

/** Futures/perp contract cell e.g. BTCUSDT vs filter "btcusdt" */
export function matchesContractSymbol(rowContract, filterContract) {
  if (!filterContract || filterContract === "all") return true;
  const rc = String(rowContract || "").replace(/\//g, "").toUpperCase();
  const fc = String(filterContract).toUpperCase();
  return rc === fc || rc.endsWith(fc);
}

/** Spot pairs "BTC/USDT" vs settlement coin usdt|usdc|busd */
export function settlementMatchesPair(pairsStr, settlement) {
  if (!settlement || settlement === "all") return true;
  const p = String(pairsStr || "").toUpperCase();
  const s = String(settlement).toUpperCase();
  return p.endsWith(`/${s}`) || p.includes(`/${s}`);
}

export function normalizePairKey(pairsStr) {
  return String(pairsStr || "").replace(/\//g, "").toUpperCase();
}

export function pairMatchesContractFilter(pairsStr, contractFilter) {
  if (!contractFilter || contractFilter === "all") return true;
  const row = normalizePairKey(pairsStr).replace(/-PERP(ETUAL)?$/i, "");
  const want = String(contractFilter).toUpperCase().replace(/\//g, "");
  return row === want;
}

export function matchesOrderType(rowOrderType, filterType) {
  if (!filterType) return true;
  const r = String(rowOrderType || "").toLowerCase();
  const f = String(filterType).toLowerCase();
  return r === f;
}

/** Perpetual contract code e.g. BTCUSDT or BTCUSDT-PERP — settlement asset suffix USDT/USDC */
export function settlementMatchesContractCode(contractStr, settlement) {
  if (!settlement || settlement === "all") return true;
  const suf = String(settlement).toUpperCase();
  const key = String(contractStr || "")
    .toUpperCase()
    .replace(/\//g, "")
    .replace(/-PERP(ETUAL)?$/i, "");
  return key.endsWith(suf);
}

/** Spot open orders "Pairs" dropdown value like btc_usdt → compare to row pair text */
export function matchesPairsDropdown(rowPairsStr, pairsFilterVal) {
  if (!pairsFilterVal || pairsFilterVal === "all") return true;
  const want = String(pairsFilterVal).replace(/_/g, "").toUpperCase();
  return normalizePairKey(rowPairsStr) === want;
}

export function matchesSideNorm(rowSide, filterSide) {
  if (!filterSide || filterSide === "all") return true;
  return String(rowSide || "").toLowerCase() === String(filterSide).toLowerCase();
}

/** Spot trade history pair label like "BTC/USDT" vs coin filter btc|eth|bnb|usdt */
export function matchesSpotPairsCoin(pairsStr, coin) {
  if (!coin || coin === "all") return true;
  const p = String(pairsStr || "").toUpperCase();
  const c = coin.toUpperCase();
  if (c === "USDT") return p.includes("USDT");
  return p.startsWith(`${c}/`);
}

export function transferWalletMatches(sel, walletLabel) {
  if (!sel || sel === "all") return true;
  const want = {
    spot: "Spot Wallet",
    futures: "Futures Wallet",
    margin: "Margin Wallet",
  }[sel];
  return want ? walletLabel === want : true;
}

/** Filter transfer history rows where `from_wallet` / `to_wallet` are API keys (e.g. spot, main, futures). */
export function transferWalletKeyMatches(sel, walletKey) {
  if (!sel || sel === "all") return true;
  const w = String(walletKey || "").trim().toLowerCase();
  const s = String(sel).trim().toLowerCase();
  return w === s;
}

export function transferCoinMatches(rowCoin, filterCoin) {
  if (!filterCoin || filterCoin === "all") return true;
  return String(rowCoin || "").toUpperCase() === String(filterCoin).toUpperCase();
}

export function referralCoinMatchesPair(pairStr, filterCoin) {
  if (!filterCoin || filterCoin === "all") return true;
  const p = String(pairStr || "").toUpperCase();
  const c = filterCoin.toUpperCase();
  return p.includes(c);
}
